import { faTag } from "@jewlr/pro-regular-svg-icons/faTag"
import {
  Box,
  Divider,
  Flex,
  FontAwesomeIcon,
  MediaBox,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { IS_AU, IS_CA, isTestMode, IS_JEWLR } from "helpers/application"
import { enableKlarnaCheckout } from "helpers/checkout/checkout_options"
import { number_with_delimiter, to_currency } from "helpers/number"
import scrollToElement from "helpers/scroll-to-element"

import WizardPricingLoading from "./loaders/wizard-pricing-loading"
import Ratings from "./ratings"
import Affirm from "./wizard-header/affirm"
import Klarna from "./wizard-header/klarna"

const Pricing = styled(Typography).attrs((props) => ({
  color: "grey.62",
  fontSize: 12,
  letterSpacing: "1px",
  ...props,
}))``

const SaleBubble = styled(Flex).attrs((props) => ({
  alignItems: "center",
  bg: props.theme.colors.sale,
  borderRadius: "6px",
  color: "white",
  display: "inline-flex",
  gap: "4px",
  mb: { _: "10px", tablet: "6px" },
  mt: { _: "12px", tablet: "8px" },
  pb: "3px",
  pt: "4px",
  px: 1,
}))``

const Sale = styled(Typography).attrs({
  fontSize: "13px",
  fontWeight: "500",
  textTransform: "uppercase",
})``

const InternalProductPrice = ({
  isMiniview,
  onSale,
  cartSummary,
  forMegaDeal,
  hideMiniview,
  prefStore,
  pricing,
  saleEnds,
  wizardLoading,
}) => {
  const [priceToDisplay, setPriceToDisplay] = useState(
    pricing.price_to_display || 0
  )

  useEffect(() => {
    let calculatedPrice = pricing.price_to_display || 0

    if (cartSummary) {
      calculatedPrice += cartSummary.cart_total
    }

    setPriceToDisplay(calculatedPrice)
  }, [cartSummary, pricing.price_to_display])

  if (!prefStore || !Object.keys(pricing).length) {
    return <WizardPricingLoading />
  } else if (priceToDisplay <= 0 || wizardLoading) {
    return ""
  }

  const percentageSavings = (
    ((pricing.retail_price - pricing.price_to_display) * 100) /
    pricing.retail_price
  ).toFixed(0)

  const priceSavings = (
    <>
      SAVE{" "}
      {`${to_currency(
        pricing.retail_price - pricing.price_to_display
      )} (${percentageSavings}%)`}
    </>
  )

  let currencyCode = ""
  if (IS_AU()) {
    currencyCode = "AU"
  } else if (IS_CA()) {
    currencyCode = "C"
  }

  const handleRatingsClick = () => {
    scrollToElement("#product-reviews", hideMiniview ? 90 : 350)
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-reviews-count-pdp",
      })
    }
  }

  return (
    <>
      {IS_JEWLR() ? (
        <Flex
          justifyContent={{ _: "center", desktop: "flex-end" }}
          mb={{ _: 2, tablet: "10px" }}
        >
          {/* Display Price */}
          <Box
            borderRight="1px solid #d9d9d9"
            lineHeight="1"
            px="6px"
            py="4px"
            textAlign="right"
          >
            {onSale && (
              <Typography bold color="sale" fontSize={16} letterSpacing="2px">
                SALE
              </Typography>
            )}
            <Typography
              color="sale"
              data-cy="product-item-price"
              fontSize="34px"
              light
              mb="-4px"
            >
              {currencyCode}
              {sl.config.default_currency}
              {number_with_delimiter(parseFloat(pricing.price_to_display))}
            </Typography>
          </Box>

          <Flex
            flexDirection="column"
            justifyContent={{ _: "flex-end", desktop: "center" }}
            lineHeight="1.2"
            px="6px"
            py="4px"
          >
            {onSale ? (
              <>
                <Box>
                  <Pricing>RETAIL {to_currency(pricing.retail_price)}</Pricing>
                </Box>
                <Box lineHeight="1">
                  <Pricing bold color="sale">
                    {priceSavings}
                  </Pricing>
                </Box>
              </>
            ) : (
              <Box lineHeight="1.4">
                <Pricing>RETAIL {to_currency(pricing.retail_price)}</Pricing>
                <Pricing color="sale">{priceSavings}</Pricing>
              </Box>
            )}
          </Flex>
        </Flex>
      ) : (
        <Typography
          color="sale"
          data-cy="product-item-price"
          fontSize="34px"
          light
          textAlign="center"
        >
          {currencyCode}
          {sl.config.default_currency}
          {number_with_delimiter(parseFloat(pricing.price_to_display))}
        </Typography>
      )}
      {sl.config.sale_key.includes("cyber_monday_holiday") ||
      (sl.config.sale_key.includes("cyber_week_holiday") && forMegaDeal) ? (
        <Box mt={-2} textAlign="center">
          <SaleBubble>
            <FontAwesomeIcon color="white" fontSize="11px" icon={faTag} />
            <Sale>
              {sl.config.sale_key.includes("cyber_monday_holiday")
                ? "CYBER MONDAY DEAL"
                : "CYBER WEEK DEAL"}
            </Sale>
          </SaleBubble>
        </Box>
      ) : (
        new Date().setHours(0, 0, 0, 0) <=
          new Date(saleEnds).setHours(0, 0, 0, 0) && (
          <Box textAlign="center">
            <Pricing color="#757575" letterSpacing="0">
              Sale ends {saleEnds}
            </Pricing>
          </Box>
        )
      )}
      {IS_JEWLR() && (
        <>
          {!isMiniview && sl.config.use_yotpo && (
            <MediaBox lessThan="tablet" mb="15px" mt={1}>
              <Ratings handleClick={handleRatingsClick} />
              <Divider mt="12px" mx={3} />
            </MediaBox>
          )}
          {sl.config.enable_affirm && !isMiniview && (
            <MediaBox lessThan="tablet" mb="10px">
              <Affirm />
            </MediaBox>
          )}
          {sl.config.klarna_osm_pdp &&
            enableKlarnaCheckout() &&
            priceToDisplay <= sl.config.klarna_max &&
            !isMiniview && (
              <MediaBox lessThan="tablet" mb="10px">
                <Klarna />
              </MediaBox>
            )}
        </>
      )}
    </>
  )
}

InternalProductPrice.propTypes = {
  cartSummary: PropTypes.object,
  fetchCart: PropTypes.func,
  forMegaDeal: PropTypes.bool,
  hideMiniview: PropTypes.bool,
  isMiniview: PropTypes.bool,
  onSale: PropTypes.bool,
  prefStore: PropTypes.bool,
  pricing: PropTypes.object,
  saleEnds: PropTypes.string,
  wizardLoading: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    cartSummary: state.cart.summary,
    forMegaDeal: state.wizard.forMegaDeal,
    hideMiniview: state.previewImageUI.miniview.scrollHide,
    onSale: state.choices.sku && !!state.choices.sku.sale_price,
    prefStore: state.wizardUI.prefStore,
    pricing: state.wizardUI.currentPricing,
    saleEnds: state.wizard.price_and_promo.sale_ends,
    wizardLoading: state.wizard.loading,
  }
}

const ProductPrice = connect(mapStateToProps)(InternalProductPrice)

export default ProductPrice
